import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "reactstrap";
import { useNotification } from "contexts/NotificationContext";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetInventories } from "../../api/inventory/service/index";
import { useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../utils/dateUtils";
import { useGetStatuses } from "api/inventory-status/service";
import { useTranslation } from "react-i18next";
import { useGetChannels } from "api/channels/service";
import classNames from "classnames";
import SearchInventoryFilters from "./components/SearchInventoryFilters";
import SearchInventoryCards from "./components/SearchInventoryCards";
import InventoryStatusTicks, {
  useInventoryStatusFilterCounts,
} from "./components/InventoryStatusTicks";
import {
  readListState,
  saveListState,
  useClearListStateOnLeave,
  LIST_STATE_FLOWS,
} from "utils/listStateSessionStorage";

const buildStatusOptions = (statusesData) => {
  const results = statusesData?.results || [];
  return results.map((s) => ({
    label: s.status_name,
    value: s.status_key,
    color: s.color,
  }));
};

const INVENTORY_LIST_STATE_KEY = "dealertiq-inventory-list-state";

const SearchInventory = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  useClearListStateOnLeave(INVENTORY_LIST_STATE_KEY, LIST_STATE_FLOWS.inventory);
  const storedStateRef = useRef(readListState(INVENTORY_LIST_STATE_KEY));
  const [search, setSearch] = useState(() => storedStateRef.current?.search ?? "");
  const [status, setStatus] = useState([]);
  const [location, setLocation] = useState(() => storedStateRef.current?.location ?? "");
  const [altLocation, setAltLocation] = useState(() => storedStateRef.current?.altLocation ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => storedStateRef.current?.search ?? "");
  const [page, setPage] = useState(() => storedStateRef.current?.page ?? 1);
  const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();
  const [pageSize, setPageSize] = useState(() => storedStateRef.current?.pageSize ?? 20);
  const statusOptions = useMemo(
    () => buildStatusOptions(statusesData),
    [statusesData]
  );

  const statusArray = useMemo(
    () => status.map((option) => option.value),
    [status]
  );

  const statusInitializedRef = useRef(false);
  const listStateHydratedRef = useRef(false);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (statusInitializedRef.current || statusOptions.length === 0) return;

    const stored = storedStateRef.current;
    if (stored?.statusValues?.length) {
      setStatus(statusOptions.filter((opt) => stored.statusValues.includes(opt.value)));
    } else {
      const defaultOption = statusOptions.find((opt) => opt.value === "available");
      if (defaultOption) setStatus([defaultOption]);
    }
    statusInitializedRef.current = true;
    setFiltersReady(true);
  }, [statusOptions]);

  useEffect(() => {
    if (!statusInitializedRef.current) return;

    if (!listStateHydratedRef.current) {
      listStateHydratedRef.current = true;
      return;
    }

    saveListState(INVENTORY_LIST_STATE_KEY, {
      search,
      statusValues: status.map((option) => option.value),
      location,
      altLocation,
      page,
      pageSize,
    });
  }, [search, status, location, altLocation, page, pageSize]);

  const { data: locationsData } = useGetChannels("sales_location_branch");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const { data: inventories, isLoading } = useGetInventories(
    debouncedSearch,
    statusArray,
    location,
    altLocation,
    page,
    pageSize,
    undefined,
    filtersReady
  );

  const serverPageCount = inventories?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  useEffect(() => {
    if (isLoading || !inventories?.pagination) return;
    const total = inventories.pagination.total ?? 0;
    const resultsLen = (inventories.results || []).length;
    if (total > 0 && resultsLen === 0 && page > 1) {
      setPage(1);
    }
  }, [isLoading, inventories?.pagination?.total, page, inventories?.results?.length]);

  const columns = useMemo(
    () => [
      {
        Header: t("searchInventory.stock"),
        accessor: "stock",
        Cell: ({ value }) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
            }}
          >
            {value}
          </div>
        ),
      },
      { Header: t("searchInventory.year"), accessor: "year" },
      { Header: t("searchInventory.make"), accessor: "make" },
      { Header: t("searchInventory.modelTrim"), accessor: "model" },
      { Header: t("searchInventory.color"), accessor: "color" },
      {
        Header: t("searchInventory.vin"),
        accessor: "vin",
        Cell: ({ value }) => (
          <div
            className="d-flex align-items-center"
            style={{
              gap: 8,
              justifyContent: "center",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <div
              title={value || undefined}
              style={{
                fontSize: 12,
                fontWeight: 600,
                minWidth: 0,
                flex: "1 1 auto",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </div>
            <Button
              color="link"
              size="sm"
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                const link = `${window.location.origin}/inventory/${value}`;
                navigator.clipboard.writeText(link);
                showNotification("success", "Link copied!");
              }}
            >
              <i className="fa fa-copy" />
            </Button>
          </div>
        ),
      },
      { Header: t("searchInventory.location"), accessor: "location" },
      { Header: t("searchInventory.daysAtLocation"), accessor: "daysAtLocation" },
      { Header: t("searchInventory.acquiredDate"), accessor: "acquiredDate" },
      {
        accessor: "status",
        disableSortBy: true,
        Header: () => (
          <div style={{ textAlign: "center", width: "100%" }}>
            {t("searchInventory.status")}
          </div>
        ),
        Cell: ({ value }) => <div className="w-100 text-center">{value}</div>,
      },
      { Header: t("searchInventory.mileage"), accessor: "mileage" },
      {
        Header: t("searchInventory.pics"),
        accessor: "pics",
        Cell: ({ value }) => <div className="w-100 text-center">{value}</div>,
      },
      {
        Header: t("searchInventory.price"),
        accessor: "price",
        Cell: ({ value }) => (
          <div
            style={{
              width: "100%",
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={typeof value === "string" ? value : undefined}
          >
            {value}
          </div>
        ),
      },
    ],
    [t, showNotification]
  );

  const inventoryData = (inventories?.results || []).map((item) => ({
    id: item.documentId,
    stock: item.stock_number,
    year: item.inventory_vehicle?.year || "",
    make: item.inventory_vehicle?.make
      ? item.inventory_vehicle.make.charAt(0).toUpperCase() +
        item.inventory_vehicle.make.slice(1).toLowerCase()
      : "",
    model: item.inventory_vehicle?.model || "",
    color: item.inventory_vehicle?.exterior_color || "",
    vin: item?.vin || "",
    location: item.location?.legal_name || "-",
    altLocation: item.alt_location?.location_name || "-",
    daysAtLocation: item.days_at_location == null ? "-" : item.days_at_location,
    acquiredDate: formatDateForDisplay(item.acquired_date),
    status: (
      <span style={{ color: item.inventory_vehicle?.vehicle_status?.color }}>
        {item.inventory_vehicle?.vehicle_status?.status_name || "-"}
      </span>
    ),
    price: new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(item.inventory_pricing?.normal_asking_price || 0),
    mileage: item?.inventory_vehicle?.actual_mileage || "",
    pics: item.images_count || 0,
  }));

  const isTheme2 = shellThemeVariant === "2";
  const InventoryTable = isTheme2 ? ReactTable2 : ReactTable;
  const statusFilterCounts = useInventoryStatusFilterCounts({
    selectedStatuses: status,
    allStatuses: statusOptions,
    results: inventories?.results || [],
    enabled: filtersReady && statusOptions.length > 0,
  });

  const filters = (
    <SearchInventoryFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      location={location}
      onLocationChange={setLocation}
      altLocation={altLocation}
      onAltLocationChange={setAltLocation}
      statusOptions={statusOptions}
      locationsData={locationsData}
      isLoadingStatuses={isLoadingStatuses}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
    >
      <InventoryTable
        activeColor={activeColor}
        data={inventoryData}
        columns={columns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={inventories?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={inventories?.pagination?.total}
        noDataText={t("searchInventory.noRecordsFound")}
        loading={isLoading}
        loadingText={t("searchInventory.loadingSearchInventory")}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSize={pageSize}
        paginationMetaExtra={<InventoryStatusTicks items={statusFilterCounts} />}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onRowClick={(row) => {
          if (row?.id) {
            navigate(`/inventory/${row.id}`);
          }
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content search-inventory-page", {
        "search-inventory-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-inventory-search-inset": isTheme2,
      })}
    >
      <SearchInventoryCards isTheme2={isTheme2} filters={filters} table={table} />
    </div>
  );
};

export default SearchInventory;
